import React from 'react';

import { importBankCsvTransactions, previewBankCsvImport } from '../../../api';
import {
    buildMappingPayload,
    getLineNumber,
    hasAnyMapping,
    isRowSelectable,
    loadMappingFromStorage,
    mappingEquals,
    persistMapping,
} from './bankCsvImportUtils';

export default function useBankCsvImport({ onImportSuccess }) {
    const [selectedFile, setSelectedFile] = React.useState(null);
    const [previewData, setPreviewData] = React.useState(null);
    const [previewWarnings, setPreviewWarnings] = React.useState([]);
    const [headers, setHeaders] = React.useState([]);
    const [mapping, setMapping] = React.useState(null);
    const [saveMapping, setSaveMapping] = React.useState(true);
    const [selection, setSelection] = React.useState(null);
    const [importResult, setImportResult] = React.useState(null);
    const [importError, setImportError] = React.useState('');
    const [loadingPreview, setLoadingPreview] = React.useState(false);
    const [loadingImport, setLoadingImport] = React.useState(false);

    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [editingRow, setEditingRow] = React.useState(null);
    const [editedPurpose, setEditedPurpose] = React.useState('');

    const autoAppliedMappingRef = React.useRef(false);

    const rows = React.useMemo(() => (Array.isArray(previewData?.rows) ? previewData.rows : []), [previewData]);

    const resetState = () => {
        setPreviewData(null);
        setPreviewWarnings([]);
        setSelection(null);
        setImportResult(null);
        setImportError('');
        setHeaders([]);
        setMapping(null);
        autoAppliedMappingRef.current = false;
    };

    const clearPreview = () => {
        setPreviewData(null);
        setPreviewWarnings([]);
        setSelection(null);
        setHeaders([]);
        setSelectedFile(null);
        setMapping(null);
        autoAppliedMappingRef.current = false;
    };

    const handleFileSelect = (file) => {
        if (!file) {
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
        resetState();
    };

    const runPreview = async (mappingOverride = null, skipStoredMapping = false) => {
        if (!selectedFile) {
            setImportError('Bitte eine CSV-Datei auswaehlen.');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);

        const mappingPayload = mappingOverride || mapping;
        const preparedMapping = mappingPayload ? buildMappingPayload(mappingPayload) : null;
        if (preparedMapping && hasAnyMapping(preparedMapping)) {
            formData.append('mapping', JSON.stringify(preparedMapping));
        }

        setLoadingPreview(true);
        setImportError('');
        setImportResult(null);
        setPreviewWarnings([]);
        setPreviewData(null);

        try {
            const res = await previewBankCsvImport(formData);
            if (res.status >= 200 && res.status < 300) {
                const payload = res.data && typeof res.data === 'object' ? res.data : null;
                setPreviewData(payload);
                setPreviewWarnings(Array.isArray(payload?.warnings) ? payload.warnings : []);
                const nextHeaders = Array.isArray(payload?.headers) ? payload.headers : [];
                setHeaders(nextHeaders);
                const responseMapping = payload?.mapping || preparedMapping || null;
                if (!mapping && responseMapping) {
                    setMapping(responseMapping);
                }
                const nextRows = Array.isArray(payload?.rows) ? payload.rows : [];
                const nextSelection = new Set();
                nextRows.forEach((row, index) => {
                    if (!isRowSelectable(row)) {
                        return;
                    }
                    nextSelection.add(getLineNumber(row, index));
                });
                setSelection(nextSelection);

                if (!skipStoredMapping && nextHeaders.length > 0) {
                    const storedMapping = loadMappingFromStorage(nextHeaders);
                    if (storedMapping && !autoAppliedMappingRef.current) {
                        autoAppliedMappingRef.current = true;
                        setMapping(storedMapping);
                        if (!mappingEquals(storedMapping, responseMapping)) {
                            await runPreview(storedMapping, true);
                            return;
                        }
                    }
                }

                if (saveMapping && nextHeaders.length > 0) {
                    const mappingToSave = preparedMapping || responseMapping;
                    if (mappingToSave && hasAnyMapping(mappingToSave)) {
                        persistMapping(nextHeaders, mappingToSave);
                    }
                }
            } else {
                const message =
                    typeof res.data === 'string' && res.data.trim().length > 0
                        ? res.data
                        : 'Vorschau fehlgeschlagen.';
                setImportError(message);
            }
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message =
                typeof backendMessage === 'string' && backendMessage.trim().length > 0
                    ? backendMessage
                    : 'Vorschau fehlgeschlagen.';
            setImportError(message);
        } finally {
            setLoadingPreview(false);
        }
    };

    const handlePreview = async () => {
        await runPreview();
    };

    const handleImport = async () => {
        if (!selectedFile) {
            setImportError('Bitte eine CSV-Datei auswaehlen.');
            return;
        }
        if (previewData && selection && selection.size === 0) {
            setImportError('Keine Datensaetze ausgewaehlt.');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);

        const selectionPayload = {};
        if (selection && selection.size > 0) {
            selectionPayload.lineNumbers = Array.from(selection);
        }

        const corrections = {};
        let hasCorrections = false;
        rows.forEach((row, index) => {
            if (row.isCorrected) {
                const lineNumber = getLineNumber(row, index);
                corrections[lineNumber] = {
                    purposeText: row.purposeText,
                };
                hasCorrections = true;
            }
        });

        if (hasCorrections) {
            selectionPayload.corrections = corrections;
        }

        if (selectionPayload.lineNumbers || selectionPayload.corrections) {
            formData.append('selection', JSON.stringify(selectionPayload));
        }

        const preparedMapping = mapping ? buildMappingPayload(mapping) : null;
        if (preparedMapping && hasAnyMapping(preparedMapping)) {
            formData.append('mapping', JSON.stringify(preparedMapping));
        }

        setLoadingImport(true);
        setImportError('');
        setImportResult(null);
        try {
            const res = await importBankCsvTransactions(formData);
            if (res.status >= 200 && res.status < 300) {
                const payload = res.data && typeof res.data === 'object' ? res.data : null;
                setImportResult(payload);
                if (onImportSuccess) {
                    onImportSuccess();
                }
                clearPreview();
            } else {
                const message =
                    typeof res.data === 'string' && res.data.trim().length > 0
                        ? res.data
                        : 'Import fehlgeschlagen.';
                setImportError(message);
            }
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message =
                typeof backendMessage === 'string' && backendMessage.trim().length > 0
                    ? backendMessage
                    : 'Import fehlgeschlagen.';
            setImportError(message);
        } finally {
            setLoadingImport(false);
        }
    };

    const handleMappingChange = (key, value) => {
        setMapping((prev) => ({
            ...buildMappingPayload(prev),
            [key]: value === '' ? null : Number(value),
        }));
        autoAppliedMappingRef.current = true;
    };

    const selectableCount = rows.filter((row) => isRowSelectable(row)).length;
    const selectedCount = selection ? selection.size : 0;
    const allSelected = selection && selectableCount > 0 && selectedCount === selectableCount;
    const someSelected = selection && selectedCount > 0 && selectedCount < selectableCount;
    const selectionEmpty = selection && selectedCount === 0;

    const handleToggleAll = () => {
        if (!selection) {
            return;
        }
        if (allSelected) {
            setSelection(new Set());
            return;
        }
        const nextSelection = new Set();
        rows.forEach((row, index) => {
            if (isRowSelectable(row)) {
                nextSelection.add(getLineNumber(row, index));
            }
        });
        setSelection(nextSelection);
    };

    const handleToggleRow = (row, index) => {
        if (!selection || !isRowSelectable(row)) {
            return;
        }
        const lineNumber = getLineNumber(row, index);
        setSelection((prev) => {
            if (!prev) {
                return prev;
            }
            const next = new Set(prev);
            if (next.has(lineNumber)) {
                next.delete(lineNumber);
            } else {
                next.add(lineNumber);
            }
            return next;
        });
    };

    const handleRowClick = (row, index) => {
        if (Array.isArray(row.errors) && row.errors.length > 0) {
            setEditingRow({ ...row, originalIndex: index });
            setEditedPurpose(row.purposeText || '');
            setEditDialogOpen(true);
        }
    };

    const handleSaveChanges = () => {
        if (!editingRow) {
            return;
        }

        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(
            (row, index) => getLineNumber(row, index) === getLineNumber(editingRow, editingRow.originalIndex)
        );

        if (rowIndex === -1) {
            setEditDialogOpen(false);
            return;
        }

        const originalRow = updatedRows[rowIndex];
        const wasSelectable = isRowSelectable(originalRow);

        const updatedRow = {
            ...originalRow,
            purposeText: editedPurpose,
            errors: [],
            isCorrected: true,
        };
        updatedRows[rowIndex] = updatedRow;

        const isNowSelectable = isRowSelectable(updatedRow);
        const lineNumber = getLineNumber(updatedRow, rowIndex);

        if (!wasSelectable && isNowSelectable) {
            setSelection((prev) => {
                const next = new Set(prev);
                next.add(lineNumber);
                return next;
            });
        }

        setPreviewData((prev) => ({
            ...prev,
            rows: updatedRows,
            errorRows: (prev.errorRows ?? 0) - 1,
            validRows: (prev.validRows ?? 0) + 1,
        }));

        setEditDialogOpen(false);
        setEditingRow(null);
    };

    return {
        selectedFile,
        previewData,
        previewWarnings,
        headers,
        mapping,
        saveMapping,
        selection,
        importResult,
        importError,
        loadingPreview,
        loadingImport,
        editDialogOpen,
        editingRow,
        editedPurpose,
        rows,
        selectableCount,
        selectedCount,
        allSelected,
        someSelected,
        selectionEmpty,
        setSaveMapping,
        setEditedPurpose,
        setEditDialogOpen,
        handleFileSelect,
        handlePreview,
        handleImport,
        clearPreview,
        handleMappingChange,
        handleToggleAll,
        handleToggleRow,
        handleRowClick,
        handleSaveChanges,
    };
}
